import { cx } from "@emotion/css";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import type { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import isMobile from "src/helpers/isMobile";
import { useFontSize } from "src/hooks/useFontSize";
import { useHideOnModalScroll } from "src/hooks/useHideOnModalScroll";
import { useLocalSearch } from "src/hooks/useLocalSearch";
import SearchIcon from "src/images/Icons/newIcons/search.svg";

interface DeckFlashcardsListProps<T extends { front: string; back: string }> {
	flashcards: T[];
	isLoading?: boolean;
	searchTestId: string;
	/** Pasek pod wyszukiwarką (np. licznik + sortowanie), chowany razem z nią. */
	toolbar?: ReactNode;
	/** Element musi mieć własny `key`. */
	renderItem: (flashcard: T, query: string) => ReactNode;
}

/** Lista fiszek zestawu w modalu: wyszukiwarka (chowana przy scrollu), skeleton, brak wyników. */
export function DeckFlashcardsList<T extends { front: string; back: string }>({
	flashcards,
	isLoading,
	searchTestId,
	toolbar,
	renderItem,
}: DeckFlashcardsListProps<T>) {
	const { t } = useTranslation();
	const { modalTextFontSizeClass } = useFontSize();
	const { anchorRef, transformClass } = useHideOnModalScroll({
		containerSelector: isMobile() ? ".modal--content" : ".deck-preview-list",
	});
	const { searchValue, setSearchValue, filteredValues, trimmedSearch } =
		useLocalSearch<T>(flashcards, ["front", "back"]);

	return (
		<div
			ref={anchorRef}
			className="flex flex-col gap-12 deck-preview-list mt-12"
		>
			<div
				className={cx(
					"modal-scroll-hide-header pb-0 flex flex-col gap-12",
					transformClass,
				)}
			>
				<TextFieldGrey
					variant="filled"
					placeholder={t(
						"modals.editDeck.searchPlaceholder",
						"Szukaj w pytaniach i odpowiedziach",
					)}
					value={searchValue}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setSearchValue(e.target.value)
					}
					className="w-100"
					onClear={() => setSearchValue("")}
					slotProps={{
						htmlInput: { "data-test-id": searchTestId },
						input: {
							disableUnderline: true,
							startAdornment: (
								<InputAdornment position="start" className="m-0 mr-8">
									<SearchIcon className="icon-normal icon-black-80" />
								</InputAdornment>
							),
						},
					}}
				/>
				{toolbar}
			</div>

			{trimmedSearch && (
				<span className={cx(modalTextFontSizeClass, "text-grey")}>
					<Trans
						i18nKey="decks.preview.searchResults"
						defaults="Wyniki dla wyszukiwania: <0>{{query}}</0>"
						values={{ query: trimmedSearch }}
						components={[<strong key="0" />]}
					/>
				</span>
			)}

			{isLoading ? (
				<div className="flex flex-col gap-12">
					{Array.from({ length: 4 }).map((_, i) => (
						<Skeleton
							key={i}
							variant="rounded"
							height={80}
							className="border-radius-16"
						/>
					))}
				</div>
			) : filteredValues.length === 0 && trimmedSearch ? (
				<div
					className={cx(
						"flex-center-row w-100 p-32 text-grey",
						modalTextFontSizeClass,
					)}
				>
					{t(
						"decks.preview.noResults",
						"Brak wyników dla wyszukiwania: {{query}}",
						{ query: trimmedSearch },
					)}
				</div>
			) : (
				<div className="flex flex-col gap-12">
					{filteredValues.map((flashcard) =>
						renderItem(flashcard, trimmedSearch),
					)}
				</div>
			)}
		</div>
	);
}
